import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import type {
  ApplicationContextItem,
  ApplicationContextGrouped,
  ApplicationContextSection,
  ApplicationContextBootstrap,
} from './types';

const jsonHeaders = { 'Content-Type': 'application/json' };

// ── Queries ──────────────────────────────────────────────────────────────

export function useApplicationContext(propertyId: string) {
  return useQuery({
    queryKey: ['application-context', propertyId],
    queryFn: async (): Promise<{
      items: ApplicationContextItem[];
      grouped: ApplicationContextGrouped;
    }> => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/context`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch application context');
      const json = await res.json();
      return json.data;
    },
  });
}

export function useContextHistory(
  propertyId: string,
  section: ApplicationContextSection | null,
  itemKey: string | null
) {
  return useQuery({
    queryKey: ['application-context-history', propertyId, section, itemKey],
    enabled: !!section && !!itemKey,
    queryFn: async (): Promise<ApplicationContextItem[]> => {
      const params = new URLSearchParams({ section: section!, itemKey: itemKey! });
      const res = await fetch(
        `${API_URL}/org/properties/${propertyId}/context/history?${params}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to fetch context history');
      const json = await res.json();
      return json.data;
    },
  });
}

export function useBootstrapStatus(propertyId: string) {
  return useQuery({
    queryKey: ['application-context-bootstrap', propertyId],
    queryFn: async (): Promise<{
      bootstrap: ApplicationContextBootstrap | null;
      hasContext: boolean;
    }> => {
      const res = await fetch(
        `${API_URL}/org/properties/${propertyId}/context/bootstrap/status`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to fetch bootstrap status');
      const json = await res.json();
      return json.data;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll while bootstrap is running
      if (data?.bootstrap?.status === 'running' || data?.bootstrap?.status === 'pending') {
        return 3000;
      }
      return false;
    },
  });
}

// ── Mutations ────────────────────────────────────────────────────────────

export function useUpsertContextItem(propertyId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      section: ApplicationContextSection;
      itemKey: string;
      itemLabel: string;
      content: string;
    }) => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/context/items`, {
        method: 'PUT',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify({ ...input, source: 'manual' }),
      });
      if (!res.ok) throw new Error('Failed to save context item');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['application-context', propertyId] });
    },
  });
}

export function useDeleteContextItem(propertyId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: { section: ApplicationContextSection; itemKey: string }) => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/context/items`, {
        method: 'DELETE',
        credentials: 'include',
        headers: jsonHeaders,
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to delete context item');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['application-context', propertyId] });
    },
  });
}

export function useCancelBootstrap(propertyId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `${API_URL}/org/properties/${propertyId}/context/bootstrap/cancel`,
        {
          method: 'POST',
          credentials: 'include',
          headers: jsonHeaders,
          body: '{}',
        }
      );
      if (!res.ok) throw new Error('Failed to cancel bootstrap');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['application-context-bootstrap', propertyId] });
      qc.invalidateQueries({ queryKey: ['application-context', propertyId] });
    },
  });
}

export function useTriggerBootstrap(propertyId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (
      input?: { credentialId?: string }
    ): Promise<{ streamId: string }> => {
      const res = await fetch(
        `${API_URL}/org/properties/${propertyId}/context/bootstrap`,
        {
          method: 'POST',
          credentials: 'include',
          headers: jsonHeaders,
          body: JSON.stringify(input ?? {}),
        }
      );
      if (!res.ok) throw new Error('Failed to trigger bootstrap');
      const json = await res.json();
      return { streamId: json.data.streamId };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['application-context-bootstrap', propertyId] });
      qc.invalidateQueries({ queryKey: ['application-context', propertyId] });
    },
  });
}
